'use client';
import React, { useState, useCallback, useMemo } from 'react';
import {
  XMarkIcon, BellAlertIcon, UserGroupIcon, PlusIcon,
  CheckIcon, ExclamationTriangleIcon, MapPinIcon, MinusIcon,
} from '@heroicons/react/24/outline';
import {
  AlertType, ALERT_TYPES, ALERT_TYPE_COLORS, ALERT_TYPE_DESCRIPTIONS,
  AlertKind, useAlerts, AlertRecipient,
} from '@/context/AlertContext';
import { DotPerson, ZONE_POLYGONS } from '@/data/dotsData';

// Point-in-polygon test (ray casting)
function pointInPolygon(lat: number, lng: number, polygon: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][1], yi = polygon[i][0];
    const xj = polygon[j][1], yj = polygon[j][0];
    const intersect = ((yi > lat) !== (yj > lat)) && (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

interface EnviarAlertaPanelProps {
  selectedDotIds: Set<string>;
  allDots: DotPerson[];
  onClose: () => void;
  onAddFromMap: () => void;
  alertKind?: AlertKind;
  areaShape?: 'polygon' | 'circle';
  areaZone?: string;
  drawnPolygon?: [number, number][] | null;
  onSelectionChange?: (ids: Set<string>) => void;
}

export default function EnviarAlertaPanel({
  selectedDotIds,
  allDots,
  onClose,
  onAddFromMap,
  alertKind = 'notification',
  areaShape,
  areaZone,
  drawnPolygon,
  onSelectionChange,
}: EnviarAlertaPanelProps) {
  const { sendAlert } = useAlerts();

  // Recipient dot IDs (starts from selection)
  const [recipientIds, setRecipientIds] = useState<Set<string>>(() => new Set(selectedDotIds));
  const [addByIdInput, setAddByIdInput] = useState('');
  const [addByIdError, setAddByIdError] = useState('');

  // Alert form
  const [alertName, setAlertName] = useState('');
  const [customName, setCustomName] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<Set<AlertType | 'otro'>>(new Set());
  const [otroText, setOtroText] = useState('');
  const [message, setMessage] = useState('');
  const [zoneFilter, setZoneFilter] = useState(areaZone ?? '');
  const [formError, setFormError] = useState('');
  const [sent, setSent] = useState(false);

  // Dots that are inside the drawn polygon (for select/deselect from polygon)
  const polygonDots = useMemo(() => {
    if (!drawnPolygon || drawnPolygon.length < 3) return [];
    return allDots.filter(d => pointInPolygon(d.lat, d.lng, drawnPolygon));
  }, [allDots, drawnPolygon]);

  const recipientDots = useMemo(
    () => allDots.filter(d => recipientIds.has(d.id)),
    [allDots, recipientIds]
  );

  const handleAddById = useCallback(() => {
    const trimmed = addByIdInput.trim();
    if (!trimmed) return;
    const dot = allDots.find(d => d.dotId === trimmed || d.id === trimmed);
    if (!dot) {
      setAddByIdError(`No se encontró el punto "${trimmed}"`);
      return;
    }
    if (recipientIds.has(dot.id)) {
      setAddByIdError('Este punto ya está en la lista');
      return;
    }
    const next = new Set([...recipientIds, dot.id]);
    setRecipientIds(next);
    onSelectionChange?.(next);
    setAddByIdInput('');
    setAddByIdError('');
  }, [addByIdInput, allDots, recipientIds, onSelectionChange]);

  const handleRemoveRecipient = useCallback((dotId: string) => {
    setRecipientIds(prev => {
      const next = new Set(prev);
      next.delete(dotId);
      onSelectionChange?.(next);
      return next;
    });
  }, [onSelectionChange]);

  // Toggle a dot from the polygon area in/out of recipients
  const handleTogglePolygonDot = useCallback((dot: DotPerson) => {
    setRecipientIds(prev => {
      const next = new Set(prev);
      if (next.has(dot.id)) {
        next.delete(dot.id);
      } else {
        next.add(dot.id);
      }
      onSelectionChange?.(next);
      return next;
    });
  }, [onSelectionChange]);

  const toggleType = useCallback((type: AlertType | 'otro') => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  const finalName = alertName === '__custom__' ? customName : alertName;

  const handleSend = useCallback(() => {
    setFormError('');
    const typesWithoutOtro = Array.from(selectedTypes).filter(t => t !== 'otro') as AlertType[];
    const hasOtro = selectedTypes.has('otro');
    const allTypes = hasOtro && otroText.trim() ? [...typesWithoutOtro, otroText.trim() as AlertType] : typesWithoutOtro;
    if (allTypes.length === 0) { setFormError('Selecciona al menos un tipo de alerta'); return; }
    if (recipientDots.length === 0) { setFormError('Agrega al menos un destinatario'); return; }

    const recipients: AlertRecipient[] = recipientDots.map(d => ({
      dotId: d.dotId,
      name: d.name,
      acknowledged: false,
    }));

    sendAlert({
      name: allTypes[0],
      types: allTypes,
      message: message.trim(),
      kind: alertKind,
      recipients,
      zone: zoneFilter || undefined,
      areaShape,
    });

    setSent(true);
  }, [selectedTypes, otroText, recipientDots, message, alertKind, zoneFilter, areaShape, sendAlert]);

  if (sent) {
    return (
      <div className="absolute top-0 right-0 h-full w-80 bg-card border-l border-border z-[1002] flex flex-col items-center justify-center gap-4 shadow-2xl">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center animate-bounce">
          <CheckIcon className="w-8 h-8 text-emerald-400" />
        </div>
        <div className="text-center px-6">
          <p className="text-base font-bold text-foreground">¡Alerta enviada!</p>
          <p className="text-sm text-emerald-400 font-semibold mt-1">
            {recipientDots.length} destinatario{recipientDots.length !== 1 ? 's' : ''} notificado{recipientDots.length !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            {Array.from(selectedTypes).join(' · ')}
          </p>
        </div>
        <div className="flex flex-col gap-2 w-full px-6 mt-2">
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-4 py-3 text-center">
            <p className="text-xs text-emerald-400 font-medium">Confirmación registrada</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{new Date().toLocaleTimeString('es-MX')}</p>
          </div>
          <button
            onClick={onClose}
            className="w-full bg-card border border-border rounded-lg py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all mt-1"
          >
            Cerrar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute top-0 right-0 h-full w-80 bg-card border-l border-border z-[1002] flex flex-col shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-orange-500/15 border border-orange-500/30 flex items-center justify-center">
            <BellAlertIcon className="w-4 h-4 text-orange-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">Enviar Alerta</p>
            <p className="text-[10px] text-muted-foreground">
              {alertKind === 'area' ? 'Alerta de área' : 'Alerta de notificación'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-muted"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">

        {/* Recipients */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
              Destinatarios ({recipientDots.length})
            </p>
            <button
              onClick={onAddFromMap}
              className="flex items-center gap-1 text-[10px] text-primary hover:underline"
            >
              <MapPinIcon className="w-3 h-3" />
              Añadir del mapa
            </button>
          </div>

          {/* Add by ID */}
          <div className="flex gap-1.5 mb-2">
            <input
              type="text"
              placeholder="ID del punto (ej. 4214)"
              value={addByIdInput}
              onChange={e => { setAddByIdInput(e.target.value); setAddByIdError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleAddById()}
              className="flex-1 bg-input border border-border rounded-lg px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring"
            />
            <button
              onClick={handleAddById}
              className="bg-primary/10 border border-primary/30 text-primary rounded-lg px-2.5 py-1.5 hover:bg-primary/20 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
            </button>
          </div>
          {addByIdError && <p className="text-[10px] text-red-400 mb-1">{addByIdError}</p>}

          {/* Recipient list */}
          <div className="space-y-1 max-h-36 overflow-y-auto">
            {recipientDots.length === 0 ? (
              <p className="text-xs text-muted-foreground/60 text-center py-3 border border-dashed border-border rounded-lg">
                Sin destinatarios
              </p>
            ) : (
              recipientDots.map(dot => (
                <div
                  key={dot.id}
                  className="flex items-center gap-2 px-2.5 py-1.5 bg-muted/30 rounded-lg border border-border/50"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
                  <span className="text-[10px] font-mono font-semibold text-foreground">#{dot.dotId}</span>
                  <span className="text-[10px] text-muted-foreground truncate flex-1">{dot.name}</span>
                  <button
                    onClick={() => handleRemoveRecipient(dot.id)}
                    className="text-muted-foreground hover:text-red-400 transition-colors shrink-0"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Polygon dot selector — shown when a polygon was drawn */}
        {polygonDots.length > 0 && (
          <div>
            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Puntos en el área ({polygonDots.length})
            </p>
            <div className="space-y-1 max-h-40 overflow-y-auto border border-orange-500/20 rounded-lg p-1.5 bg-orange-500/5">
              {polygonDots.map(dot => {
                const isIn = recipientIds.has(dot.id);
                return (
                  <button
                    key={dot.id}
                    onClick={() => handleTogglePolygonDot(dot)}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md border text-left transition-all duration-100 ${
                      isIn
                        ? 'bg-orange-500/15 border-orange-500/30 text-foreground'
                        : 'bg-muted/20 border-border/40 text-muted-foreground hover:bg-muted/40'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isIn ? 'bg-orange-500 border-orange-500' : 'border-border'
                    }`}>
                      {isIn ? <CheckIcon className="w-2.5 h-2.5 text-white" /> : <MinusIcon className="w-2.5 h-2.5 text-muted-foreground/40" />}
                    </div>
                    <span className="text-[10px] font-mono font-semibold">#{dot.dotId}</span>
                    <span className="text-[10px] truncate flex-1">{dot.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Alert types (multi-select) */}
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Tipo(s) de alerta
          </p>
          <div className="space-y-1">
            {ALERT_TYPES.map(type => {
              const isSelected = selectedTypes.has(type);
              const color = ALERT_TYPE_COLORS[type];
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border text-left transition-all duration-100 ${
                    isSelected
                      ? 'border-opacity-60' :'border-border bg-muted/20 hover:bg-muted/40'
                  }`}
                  style={isSelected ? { borderColor: color + '80', background: color + '15' } : {}}
                >
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isSelected ? 'border-transparent' : 'border-border'
                    }`}
                    style={isSelected ? { background: color } : {}}
                  >
                    {isSelected && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold" style={{ color: isSelected ? color : undefined }}>
                      {type}
                    </p>
                    <p className="text-[9px] text-muted-foreground truncate">{ALERT_TYPE_DESCRIPTIONS[type]}</p>
                  </div>
                </button>
              );
            })}
            {/* Otro option */}
            <button
              onClick={() => toggleType('otro')}
              className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg border text-left transition-all duration-100 ${
                selectedTypes.has('otro')
                  ? 'border-purple-500/60 bg-purple-500/15' : 'border-border bg-muted/20 hover:bg-muted/40'
              }`}
            >
              <div
                className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                  selectedTypes.has('otro') ? 'border-transparent bg-purple-500' : 'border-border'
                }`}
              >
                {selectedTypes.has('otro') && <CheckIcon className="w-2.5 h-2.5 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold" style={{ color: selectedTypes.has('otro') ? '#a855f7' : undefined }}>
                  Otro
                </p>
                <p className="text-[9px] text-muted-foreground">Especificar tipo personalizado</p>
              </div>
            </button>
            {selectedTypes.has('otro') && (
              <input
                type="text"
                placeholder="Describe el tipo de alerta…"
                value={otroText}
                onChange={e => setOtroText(e.target.value)}
                className="w-full bg-input border border-purple-500/30 rounded-lg px-2.5 py-2 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-purple-500/50 mt-1"
              />
            )}
          </div>
        </div>

        {/* Zone filter */}
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Zona (opcional)</p>
          <select
            value={zoneFilter}
            onChange={e => setZoneFilter(e.target.value)}
            className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">Sin filtro de zona</option>
            {ZONE_POLYGONS.map(z => (
              <option key={z.id} value={z.name}>{z.name}</option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Mensaje</p>
          <textarea
            placeholder="Escribe el mensaje de la alerta…"
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={3}
            className="w-full bg-input border border-border rounded-lg px-2.5 py-2 text-xs text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-ring resize-none"
          />
        </div>

        {formError && (
          <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            <ExclamationTriangleIcon className="w-3.5 h-3.5 text-red-400 shrink-0" />
            <p className="text-xs text-red-400">{formError}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-border shrink-0 space-y-2">
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <UserGroupIcon className="w-3.5 h-3.5" />
          <span>{recipientDots.length} destinatario{recipientDots.length !== 1 ? 's' : ''}</span>
          {selectedTypes.size > 0 && (
            <>
              <span>·</span>
              <span>{selectedTypes.size} tipo{selectedTypes.size !== 1 ? 's' : ''} seleccionado{selectedTypes.size !== 1 ? 's' : ''}</span>
            </>
          )}
        </div>
        <button
          onClick={handleSend}
          className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-2.5 text-sm font-semibold transition-all active:scale-95 shadow-lg shadow-orange-500/20"
        >
          <BellAlertIcon className="w-4 h-4" />
          Enviar Alerta
        </button>
      </div>
    </div>
  );
}
